import React, { useState } from 'react';
import { useMap } from '../../contexts/MapContext';

export default function DoorPanel({ doorId, onClose }) {
  const { doors, updateDoor, deleteDoor } = useMap();
  const [damageInput, setDamageInput] = useState('');

  const door = doors.find((d) => d.id === doorId);
  if (!door) return null;

  const isSecret   = door.type === 'secret';
  const isBars     = door.type === 'bars';
  const state      = door.state || 'closed';
  const hp         = door.hp ?? 15;
  const maxHp      = door.maxHp ?? 15;
  const hpPct      = Math.max(0, hp / maxHp);
  const hpColor    = hpPct > 0.5 ? '#5cb870' : hpPct > 0.25 ? '#d4943a' : '#e07070';

  const applyDamage = () => {
    const dmg = parseInt(damageInput, 10);
    if (isNaN(dmg) || dmg <= 0) return;
    const newHp = Math.max(0, hp - dmg);
    updateDoor(doorId, { hp: newHp, ...(newHp <= 0 ? { state: 'open' } : {}) });
    setDamageInput('');
  };

  const repairFull = () => updateDoor(doorId, { hp: maxHp });

  const handleDelete = () => { deleteDoor(doorId); onClose(); };

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.title}>
          {isSecret ? '🔍 Secret Door' : isBars ? '⛓ Bars' : '🚪 Door'}
        </span>
        <button style={styles.closeBtn} onClick={onClose}>✕</button>
      </div>

      {/* State */}
      <div style={styles.section}>
        <div style={styles.label}>State</div>
        <div style={styles.stateRow}>
          {['open', 'closed', 'locked'].map((s) => (
            <button
              key={s}
              style={{ ...styles.stateBtn, ...(state === s ? styles.stateActive : {}) }}
              onClick={() => updateDoor(doorId, { state: s })}
            >
              {s === 'open' ? '🚪 Open' : s === 'closed' ? '🚪 Closed' : '🔒 Locked'}
            </button>
          ))}
        </div>
      </div>

      {/* HP */}
      <div style={styles.section}>
        <div style={styles.label}>Hit Points</div>
        <div style={styles.hpBarOuter}>
          <div style={{ ...styles.hpBarFill, width: `${hpPct * 100}%`, background: hpColor }} />
        </div>
        <div style={styles.hpNumbers}>
          <span style={{ color: hpColor, fontWeight: 700 }}>{hp}</span>
          <span style={styles.dim}> / {maxHp}</span>
          {hp <= 0 && <span style={styles.destroyed}> DESTROYED</span>}
        </div>
        <div style={styles.inputRow}>
          <input
            style={styles.numInput}
            type="number"
            min="1"
            placeholder="dmg"
            value={damageInput}
            onChange={(e) => setDamageInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyDamage()}
          />
          <button className="btn-danger btn-sm" style={{ flex: 1 }} onClick={applyDamage}>Damage</button>
          <button className="btn-ghost btn-sm" onClick={repairFull}>Full Repair</button>
        </div>
      </div>

      {/* Secret door reveal */}
      {isSecret && !door.secretRevealed && (
        <div style={styles.section}>
          <div style={styles.label}>Secret Door</div>
          <p style={styles.hint}>Players see this as a <strong>{door.secretWallStyle || 'stone'}</strong> wall. Only you can see the purple overlay.</p>

          {/* Discovery DC — per-player perception threshold */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <label style={{ fontSize: 11, color: '#9a8b6e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
              Discovery DC
            </label>
            <input
              type="number"
              min={1}
              max={40}
              value={door.discoveryDC ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                updateDoor(doorId, { discoveryDC: Number.isFinite(v) ? v : null });
              }}
              placeholder="—"
              style={{
                width: 64, padding: '6px 8px', background: '#0e1528',
                border: '1px solid #4a3d28', borderRadius: 4,
                color: '#c9b3e8', fontSize: 13, fontWeight: 700,
                fontFamily: "'Press Start 2P', monospace", textAlign: 'center',
              }}
              onPointerDown={(e) => e.stopPropagation()}
            />
            <span style={{ fontSize: 10, color: '#6a5d8a', flex: 1, lineHeight: 1.3 }}>
              Empty = manual reveal only.
            </span>
          </div>

          <button
            className="btn-primary w-full"
            style={{ marginTop: 8 }}
            onClick={() => updateDoor(doorId, {
              secretRevealed: true,
              style: 'wood',
              state: 'locked',
            })}
          >
            👁 Reveal to All Players (wood, locked)
          </button>
        </div>
      )}

      {/* Delete */}
      <div style={styles.section}>
        <button className="btn-danger btn-sm w-full" onClick={handleDelete}>🗑 Delete Door</button>
      </div>
    </div>
  );
}

const styles = {
  panel:      { background: '#151525', border: '1px solid #d4a84744', borderRadius: 4, overflow: 'hidden' },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #4a3d28', background: '#142038' },
  title:      { fontWeight: 700, fontSize: 13 },
  closeBtn:   { background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 16, cursor: 'pointer', padding: '0 2px' },
  section:    { padding: '10px 14px', borderBottom: '1px solid #4a3d28' },
  label:      { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9a8b6e', marginBottom: 8 },
  stateRow:   { display: 'flex', gap: 5, flexWrap: 'wrap' },
  stateBtn:   { flex: 1, padding: '6px 4px', fontSize: 11, fontWeight: 600, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#aaaacc', cursor: 'pointer', whiteSpace: 'nowrap' },
  stateActive:{ background: '#3a2060', borderColor: '#d4a847', color: '#f0e6d0' },
  hpBarOuter: { height: 6, background: '#1a2a48', borderRadius: 3, overflow: 'hidden', marginBottom: 4 },
  hpBarFill:  { height: '100%', borderRadius: 3, transition: 'width 0.3s' },
  hpNumbers:  { display: 'flex', alignItems: 'baseline', marginBottom: 8, fontSize: 14 },
  dim:        { color: '#9a8b6e', fontSize: 12 },
  destroyed:  { color: '#e07070', fontSize: 11, marginLeft: 6 },
  inputRow:   { display: 'flex', gap: 6, alignItems: 'center' },
  numInput:   { width: 56, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '6px 8px', fontSize: 13, outline: 'none', textAlign: 'center' },
  hint:       { fontSize: 12, color: '#9a8b6e', lineHeight: 1.4 },
};
